import React, { ComponentPropsWithoutRef, MouseEvent, ReactNode, useState } from 'react';

import cx from 'classnames';
import { prop } from 'ramda';

import styles from './index.module.css';

type TabsProps = {
  className?: string;
  segmented?: boolean;
  defaultSelected?: ReturnType<typeof getId>;
  children: React.ReactElement<PanelProps>[];
  value?: string;
  onChange?: (id: string, e: MouseEvent<HTMLLIElement>) => void;
  componentsProps?: {
    wrapper?: ComponentPropsWithoutRef<'div'>;
    tabs?: ComponentPropsWithoutRef<'ul'>;
    tab?: Omit<ComponentPropsWithoutRef<'li'>, 'onClick'> & {
      onClick?: (e: React.MouseEvent<HTMLLIElement>, id: string) => void;
    };
    tabUnderline?: ComponentPropsWithoutRef<'span'>;
  };
};

function getId(element: TabsProps['children'][number]) {
  return element.props.id;
}

export function Tabs(props: TabsProps) {
  const { children, segmented, defaultSelected, value, onChange, componentsProps } = props;
  const [selected, setSelected] = useState<string | null>(defaultSelected || getId(children[0]));

  const currentValue = value ?? selected;

  const handleChange = (id: string, e: MouseEvent<HTMLLIElement>) => {
    if (onChange) {
      if (id !== currentValue) onChange(id, e);
    }
    setSelected(id);
  };

  const selectedIndex = children.findIndex((elem) => getId(elem) === currentValue);

  return (
    <div
      {...componentsProps?.wrapper}
      className={cx({ [styles.segmented]: segmented }, componentsProps?.wrapper?.className)}
    >
      <ul {...componentsProps?.tabs} className={cx(styles.list, componentsProps?.tabs?.className)}>
        {children.map((elem) => {
          const id = getId(elem);
          const isActive = id === currentValue;
          return (
            <li
              {...componentsProps?.tab}
              id={id}
              key={id}
              className={cx(
                styles.item,
                isActive && `${prop('itemActive', styles)} active`,
                componentsProps?.tab?.className,
              )}
              onClick={(e) => {
                componentsProps?.tab?.onClick?.(e, id);
                handleChange(id, e);
              }}
            >
              <span
                {...componentsProps?.tabUnderline}
                className={cx(styles.underline, componentsProps?.tabUnderline?.className)}
              />
              {elem.props.title}
            </li>
          );
        })}
      </ul>

      {children[selectedIndex]}
    </div>
  );
}

type PanelProps = {
  id: string;
  title: string | ReactNode;
  children: JSX.Element;
};

function Panel({ children }: PanelProps): JSX.Element {
  return children;
}

Tabs.Panel = Panel;
